import { activeWorld } from "src/utils/world"
import { relationOf } from "src/utils/teamColors"

/* The round, played back (requirement 36, M1–M7).

   The server records what `process_round` did, in the order it did it — `GameMetadata.motion`:
   the `stacks` that moved, fought or landed, and one list of `steps` (strikes, tunnel orders,
   discharges, every cell a stack took with its `pass`, the contacts, the deeds taken in passing,
   the landings, the battle rounds). It rides on the `game_next_round` message beside the trail,
   redacted per side (A5, A6), and this module turns it into a **timeline**: cues with an `at` and
   a `duration` in milliseconds, and a position for every stack at any moment.

   Pure, and no React: `buildTimeline` is run once when the round lands and `stateAt` once a frame
   by the canvas, so what the canvas draws and what `buildBoard` hides are read off the same clock.

   The order is the engine's own, never re-sorted (A4): strikes, tunnels and discharges together
   before any move; then the passes on one steady clock — pass k is the k-th `STEP_MS` of the walk,
   every move of a pass taking it together; a contact at the moment the stepping stack reaches the
   cell; a battle from the end of its pass; and the carried-over battles, fought after the walk,
   last. A chase catches or misses on screen exactly as the server decided, because the passes are
   its initiative made visible.

   **The walk never stops for a fight** (owner, 2026-10-26: *"every X ms the units move 1 cell,
   smoothly (the units don't stop at combat, as this event is instant)"*). A contact and a clash are
   instants the effects draw over the walk; the clock of the passes does not wait for them. And a
   stack that sits a pass out — a slow one, which the server steps fewer times than the fastest —
   walks its cell over the whole gap rather than stepping and waiting, so it arrives at the moment
   the server says and moves at an even pace on the way (`MAX_STRETCH_STEPS` bounds the stretch, so
   a stack held by a fight and sent on later does not crawl through it). */

// About a cell a second, the pace of the formations prototype the owner chose (2026-10-26); it was
// 180, then 270. The cap moved with it.
export const STEP_MS = 900
// A stack that took no step for a while walks its next cell over at most this many steps.
export const MAX_STRETCH_STEPS = 3
export const CLASH_MS = 650
export const BLAST_MS = 700
export const TUNNEL_MS = 600
export const DISCHARGE_MS = 500
export const MAX_PLAY_MS = 15000

const PRE_MOVE = { strike: ["blast", BLAST_MS], tunnel: ["tunnel", TUNNEL_MS], discharge: ["discharge", DISCHARGE_MS] }

const keyOf = (cell) => `${cell[0]}-${cell[1]}`

/* `cell` put beside `near` across the seam the short way: a step from 199 to 0 is one cell east,
   and a stack that has already walked past the seam (its column is 203) keeps walking in the same
   tile. The rows never wrap. The canvas wraps the fractional column back when it draws. */
const unwrapNear = (cell, near, columns) => {
  const [col, row] = cell
  if (!near) return [col, row]
  return [col + Math.round((near[0] - col) / columns) * columns, row]
}

const isCell = (value) => Array.isArray(value) && value.length >= 2

export const buildTimeline = (
  motion,
  { columns = activeWorld().columns, teamOf = () => null, viewerId = null, standoffs = null } = {}
) => {
  const steps = Array.isArray(motion?.steps) ? motion.steps : []
  const stacks = {}
  const stackFor = (id, raw = {}) => {
    if (!stacks[id]) {
      const team = teamOf(raw.user_id) ?? null
      stacks[id] = {
        id: String(id),
        userId: raw.user_id ?? null,
        team,
        relation: raw.user_id == null ? "enemy" : relationOf({ ownerId: raw.user_id, userId: viewerId, teamOf }),
        // What the stack is made of, as the log sent it: the formation is drawn from it
        // (`utils/formation`); an enemy's comes whole too since 36/V2.
        units: raw.units || null,
        start: isCell(raw.start) ? [raw.start[0], raw.start[1]] : null,
        // A garrison on a fought site (owner, 2026-10-23): it takes no step, it stands where it
        // stood until the clash there has been shown, since the live board is the one after the
        // fight. On a city the city's icon stands for it and no sprite is drawn (`owned`).
        garrison: Boolean(raw.garrison),
        owned: Boolean(raw.owned),
        // The stack came into the side's sight on the road (A6): no start to stand on, and the
        // sprite fades in along its first visible step.
        fromOutOfSight: false,
        track: [],
        release: null,
        // Its road's end is known to this side: it landed or fought where its log ends (below), or
        // it is the side's own. An enemy that walked out of sight has neither, and its line ends
        // with no arrowhead (owner, 2026-10-26).
        endKnown: false,
      }
    }
    return stacks[id]
  }
  Object.entries(motion?.stacks || {}).forEach(([id, raw]) => stackFor(id, raw || {}))

  const cues = []
  let anyMove = false
  let preEnd = 0
  let cursor = 0
  let pass = null
  let passStart = 0
  let passMovesEnd = 0
  let passEnd = 0
  const lastMoveEnd = {}
  const contactAt = {}
  const sitesNew = new Set()

  steps.forEach((step) => {
    if (!step || typeof step !== "object") return
    const at = isCell(step.at) ? step.at : null
    if (PRE_MOVE[step.kind]) {
      if (!at) return
      const [kind, duration] = PRE_MOVE[step.kind]
      // Before any move, all of them together; one logged after the walk (it should not be) plays
      // where the walk has got to.
      const start = anyMove ? passEnd : 0
      cues.push({ kind, at: start, duration, col: at[0], row: at[1], by: step.by ?? null, direction: step.direction ?? null })
      if (anyMove) passEnd = Math.max(passEnd, start + duration)
      else preEnd = Math.max(preEnd, duration)
      return
    }
    if (step.kind === "move") {
      // A step naming a stack the side never saw (the server drops such a stack from `stacks`
      // entirely, A6) is not drawn: there is no sprite to move.
      if (!isCell(step.to) || !stacks[String(step.id)]) return
      if (!anyMove) {
        anyMove = true
        cursor = Math.max(cursor, preEnd)
      }
      if (pass !== step.pass) {
        // The next pass starts as this one's moves end: a fight never holds the clock.
        if (pass !== null) cursor = passMovesEnd
        pass = step.pass
        passStart = cursor
        passMovesEnd = passStart
      }
      const stack = stackFor(step.id)
      const end = passStart + STEP_MS
      // The cell is walked from the stack's last arrival, when it sat passes out (a slower stack),
      // so its pace stays even; it still arrives when the server says.
      const lastArrival = lastMoveEnd[stack.id]
      const start = lastArrival == null ? passStart : Math.max(lastArrival, end - MAX_STRETCH_STEPS * STEP_MS)
      const duration = end - start
      const previous = stack.track.length ? stack.track[stack.track.length - 1].to : stack.start
      const rawFrom = isCell(step.from) ? step.from : step.to
      const from = unwrapNear(rawFrom, previous, columns)
      const to = unwrapNear(step.to, from, columns)
      const fadeIn = Boolean(step.from_out_of_sight) && stack.track.length === 0 && !stack.start
      if (fadeIn) stack.fromOutOfSight = true
      const segment = { at: start, duration, from, to, index: stack.track.length, fadeIn }
      stack.track.push(segment)
      cues.push({ kind: "move", at: start, duration, id: stack.id, from, to, pass: step.pass, index: segment.index })
      lastMoveEnd[stack.id] = end
      passMovesEnd = Math.max(passMovesEnd, end)
      passEnd = Math.max(passEnd, passMovesEnd)
      return
    }
    if (step.kind === "contact") {
      if (!at) return
      const named = (step.ids || []).map(String)
      const stepper = named[named.length - 1]
      const ids = named.filter((id) => stacks[id])
      // The moment the stepping sprite reaches the cell (M2): the end of its move.
      const when = lastMoveEnd[stepper] ?? passMovesEnd
      const key = keyOf(at)
      contactAt[key] = when
      sitesNew.add(key)
      cues.push({ kind: "contact", at: when, duration: 0, col: at[0], row: at[1], ids, pass: step.pass })
      ids.forEach((id) => {
        stacks[id].endKnown = true
      })
      passEnd = Math.max(passEnd, when)
      return
    }
    if (step.kind === "taken" || step.kind === "land") {
      if (!at) return
      const id = String(step.id)
      if (!stacks[id]) return
      const key = keyOf(at)
      let when = lastMoveEnd[id] ?? (anyMove ? passMovesEnd : preEnd)
      // A victor whose road ended at the city it fought for lands *after* the clash: the server
      // logs its `land` right after the `battle`, and the sprite stands at the gates through the
      // clash and goes in once it has been shown.
      cues.forEach((cue) => {
        if (cue.kind === "clash" && keyOf([cue.col, cue.row]) === key && cue.at + cue.duration > when) {
          when = cue.at + cue.duration
        }
      })
      const cue = { kind: step.kind, at: when, duration: 0, id, col: at[0], row: at[1] }
      if (step.kind === "taken") cue.fromOwner = step.from_owner ?? null
      cues.push(cue)
      if (step.kind === "land") {
        const stack = stackFor(id)
        stack.endKnown = true
        stack.release = stack.release == null ? when : Math.min(stack.release, when)
      }
      return
    }
    if (step.kind === "lost") {
      // A deed leaving (owner, 2026-10-23: "the cities disappear before the stack gets there"):
      // the board under the show no longer has the city, so the old owner's is drawn until this
      // plays — where the walk has got to, or after the clash that took it.
      if (!at) return
      const key = keyOf(at)
      let when = anyMove ? passMovesEnd : preEnd
      cues.forEach((c) => {
        if (c.kind === "clash" && keyOf([c.col, c.row]) === key && c.at + c.duration > when) when = c.at + c.duration
      })
      cues.push({ kind: "lost", at: when, duration: 0, col: at[0], row: at[1], fromOwner: step.from_owner ?? null })
      return
    }
    if (step.kind === "battle") {
      if (!at) return
      const key = keyOf(at)
      // After the last move of its pass, and never before the contact at its site. Battles of one
      // pass — and the carried-over ones after the walk — share a start and play in parallel.
      const base = anyMove ? passMovesEnd : preEnd
      const when = Math.max(base, contactAt[key] ?? 0)
      cues.push({
        kind: "clash",
        at: when,
        duration: CLASH_MS,
        col: at[0],
        row: at[1],
        ended: Boolean(step.ended),
        winnerTeam: step.winner_team ?? null,
        // The sides on the site, when the server says (a sorted list of team values): what the
        // battle sound is decided on, since a garrison is not a stack the log names.
        teams: Array.isArray(step.teams) ? step.teams : null,
        // A battle burning since an earlier round keeps its fire through the walk, even when a
        // stack reaches it this round and logs a contact there (owner, 2026-10-23: "the fire should
        // not disappear in a city that was already fighting").
        newThisRound: sitesNew.has(key) && !step.carried_over,
      })
      // It plays over the walk and holds nobody (owner, 2026-10-26): only the show's end waits for it.
      passEnd = Math.max(passEnd, when + CLASH_MS)
      if (!anyMove) preEnd = Math.max(preEnd, when + CLASH_MS)
    }
  })

  let total = Math.max(preEnd, passEnd, cursor, ...cues.map((cue) => cue.at + cue.duration), 0)

  /* When the live board takes a stack back (M4, M6): once it has landed; once the battle at the
     cell it ends on has been shown; else when the show is over. Until then its live icon is hidden
     and the timeline draws it. */
  Object.values(stacks).forEach((stack) => {
    if (stack.release != null) return
    const last = stack.track[stack.track.length - 1]
    const finalCell = last ? last.to : stack.start
    const arrival = last ? last.at + last.duration : 0
    if (finalCell) {
      const key = keyOf([wrapColumn(finalCell[0], columns), finalCell[1]])
      const clash = cues.find((cue) => cue.kind === "clash" && keyOf([cue.col, cue.row]) === key && cue.at + 1e-6 >= arrival)
      if (clash) {
        stack.release = clash.at + clash.duration
        return
      }
    }
    stack.release = total
  })

  // Down to the cap, never up (M2): a long round is a quick round, not a round cut short.
  const scale = total > MAX_PLAY_MS ? MAX_PLAY_MS / total : 1
  if (scale < 1) {
    cues.forEach((cue) => {
      cue.at *= scale
      cue.duration *= scale
    })
    Object.values(stacks).forEach((stack) => {
      stack.release *= scale
      stack.track.forEach((segment) => {
        segment.at *= scale
        segment.duration *= scale
      })
    })
    total *= scale
  }

  // Cells where both teams stand when the round is over with no fight there yet, with each side's
  // units (B6, `mapBoard.standoffCells`): fought on screen from the last arrival to the show's end.
  // Whose each side is to the viewer, for the fights' feet (S6); none for a viewer with no side.
  const mine = viewerId == null ? null : teamOf(viewerId)
  const relations = mine ? { [mine]: "self", [mine === "human" ? "zombie" : "human"]: "enemy" } : {}
  return { cues, stacks, total, scale, columns, standoffs: standoffs || {}, relations }
}

const wrapColumn = (col, columns) => ((col % columns) + columns) % columns

/* Where a stack is at `t`, unwrapped: on its start before it moves, between two cells while it
   walks, on the last cell it took afterwards. `null` while it is still in the fog. `angle` is the
   way the formation faces (radians, 0 east, screen y down): the road it is on, or the one it is
   about to take, turned through a corner over the first `TURN_SHARE` of the cell rather than
   snapped; `walking` is true only between two cells. */
const TURN_SHARE = 0.4
const headingOf = (segment) => Math.atan2(segment.to[1] - segment.from[1], segment.to[0] - segment.from[0])
const moves = (segment) => segment.to[0] !== segment.from[0] || segment.to[1] !== segment.from[1]
const turnBetween = (from, to, share) => {
  const delta = ((((to - from) % (2 * Math.PI)) + 3 * Math.PI) % (2 * Math.PI)) - Math.PI
  return from + delta * Math.min(1, share)
}

const positionAt = (stack, t) => {
  const { track, start } = stack
  if (track.length === 0) {
    return start ? { col: start[0], row: start[1], angle: 0, walking: false, alpha: 1, ahead: [], along: 0 } : null
  }
  // The cells still to walk in this round from segment `index` on, and how far the stack has come.
  const aheadFrom = (index) => track.slice(index).filter(moves).map((segment) => segment.to)
  let walked = 0
  let angle = headingOf(track.find(moves) || track[0])
  for (const [index, segment] of track.entries()) {
    const before = angle
    if (moves(segment)) angle = headingOf(segment)
    if (t < segment.at) {
      if (segment.index === 0 && stack.fromOutOfSight) return null
      return { col: segment.from[0], row: segment.from[1], angle, walking: false, alpha: 1, ahead: aheadFrom(index), along: walked }
    }
    if (t < segment.at + segment.duration) {
      const progress = segment.duration > 0 ? (t - segment.at) / segment.duration : 1
      return {
        col: segment.from[0] + (segment.to[0] - segment.from[0]) * progress,
        row: segment.from[1] + (segment.to[1] - segment.from[1]) * progress,
        angle: turnBetween(before, angle, progress / TURN_SHARE),
        walking: true,
        alpha: segment.fadeIn ? progress : 1,
        ahead: aheadFrom(index),
        along: walked + (moves(segment) ? progress : 0),
      }
    }
    if (moves(segment)) walked += 1
  }
  const last = track[track.length - 1]
  return { col: last.to[0], row: last.to[1], angle, walking: false, alpha: 1, ahead: [], along: walked }
}

/* Where every stack of the show ended up facing: `{"<userId>@<col>-<row>": angle}`, the cell wrapped,
   for each stack that took a step (owner, 2026-10-26: keep the orientation the last movement left
   them in). The board has no direction of its own — the server cuts a march's road every round —
   so this is what a camp faces until a later show turns it. */
export const finalFacings = (timeline) => {
  const facings = {}
  if (!timeline) return facings
  Object.values(timeline.stacks).forEach((stack) => {
    if (stack.userId == null) return
    const walked = stack.track.filter(moves)
    if (!walked.length) return
    const last = walked[walked.length - 1]
    const end = stack.track[stack.track.length - 1].to
    facings[`${stack.userId}@${wrapColumn(end[0], timeline.columns)}-${end[1]}`] = headingOf(last)
  })
  return facings
}

/* The same, for a past board (owner, 2026-10-26, game 249 round 23: a replay drew the helicopters
   that had just flown north facing east). A live browser keeps what each show left
   (`rememberFacings`); a replay or a look-back has no such memory, so it is rebuilt from the logs of
   every round up to the one shown, a later round's heading over an earlier one's — what a viewer
   who had watched every show would have kept. `{tick: facings}`, one entry per step, so a stepper
   moving through the rounds reads it rather than rebuilding it. */
export const facingsByTick = (steps, { columns = activeWorld().columns } = {}) => {
  const byTick = {}
  let running = {}
  ;[...(steps || [])]
    .filter((step) => step?.tick != null)
    .sort((a, b) => a.tick - b.tick)
    .forEach((step) => {
      const motion = step.metadata?.motion
      if (motion?.steps?.length) running = { ...running, ...finalFacings(buildTimeline(motion, { columns })) }
      byTick[step.tick] = running
    })
  return byTick
}

/* The show's clock, and its speed (owner, 2026-10-26: "configure the speed of the troops ... x1, if
   we press, x2, otherwise x4"). A show playing is `{timeline, startedAt, base, speed}`: at `now` it is
   at `base + (now - startedAt) × speed` ms of its timeline. A change of speed starts a new leg from
   where the show is (`atSpeed`), so nothing jumps. The timeline itself never changes. */
export const SHOW_SPEEDS = [1, 2, 4]
export const nextShowSpeed = (speed) => SHOW_SPEEDS[(SHOW_SPEEDS.indexOf(speed) + 1) % SHOW_SPEEDS.length]
export const clockNow = () => (typeof performance === "object" ? performance.now() : Date.now())
export const showTimeAt = (show, now = clockNow()) => (show.base || 0) + (now - show.startedAt) * (show.speed || 1)
export const atSpeed = (show, speed, now = clockNow()) => ({ ...show, base: showTimeAt(show, now), startedAt: now, speed })

/* The show at `t` ms: the sprites to draw (fractional, unwrapped cells), the effects playing and
   how far each has got, what the live board must not draw yet, and whether it is over. */
export const stateAt = (timeline, t) => {
  const sprites = []
  const effects = []
  const movementIds = new Set()
  const landedCells = new Set()
  const clashedSites = new Set()
  const garrisonCells = new Set()
  // Cells whose deed leaves later in the show: "col-row" → the owner it leaves, drawn as it was.
  const lostCells = {}
  /* What is still to land on each of those cells: "col-row" → {userId: {unit: n}}, the landing
     stacks' units as the log sent them. The board takes only these off what it draws there, so a
     camp that stood on the cell before the round stays (game 250, round 17: a second column landing
     on 36-16 hid the first, and the show read as last round's march played again). */
  const landingUnits = {}
  const fights = []
  if (!timeline) return { sprites, effects, fights, hiddenLive: { movementIds, landedCells, landingUnits, clashedSites, garrisonCells, lostCells }, done: true }

  /* The battles of the round, fought on screen until their clash (owner, 2026-10-26: "The units
     should appear combating until all movements have finished. This matches the backend engine").
     A battle burning since an earlier round is fought from the start of the show — the engine
     fights it after every movement — and one that breaks out this round from its first contact;
     both until their last clash. While a site fights, the stacks standing on it are its armies,
     drawn as the fight rather than one by one, and the live board's own battle there waits. */
  const contactAt = {}
  const windows = {}
  const lastContactAt = {}
  timeline.cues.forEach((cue) => {
    const key = `${cue.col}-${cue.row}`
    if (cue.kind !== "contact") return
    contactAt[key] = Math.min(contactAt[key] ?? Infinity, cue.at)
    lastContactAt[key] = Math.max(lastContactAt[key] ?? -Infinity, cue.at)
  })
  timeline.cues.forEach((cue) => {
    if (cue.kind !== "clash") return
    const key = `${cue.col}-${cue.row}`
    const window = (windows[key] = windows[key] || { col: cue.col, row: cue.row, start: Infinity, end: 0 })
    window.end = Math.max(window.end, cue.at + cue.duration)
    window.start = Math.min(window.start, cue.newThisRound ? Math.min(contactAt[key] ?? cue.at, cue.at) : 0)
  })
  /* Both teams standing on a cell when the round is over, with no fight there this round or one
     already fought (owner, 2026-10-26: a stack that reaches a site after its battle of this round
     halts there and fights next round). They are drawn fighting from the last arrival there to the
     end of the show, and the board draws the fight on after it (36/B6). */
  const standoffs = timeline.standoffs || {}
  const standoffFrom = {}
  Object.keys(standoffs).forEach((key) => {
    const after = windows[key] ? windows[key].end : -Infinity
    const arrival = lastContactAt[key]
    // From a late arrival after this round's fight there; else on from the fight's clash, both
    // sides still standing; else, with no fight there this round, from the last contact.
    standoffFrom[key] = arrival != null && arrival >= after ? arrival : windows[key] ? after : arrival ?? 0
    if (!windows[key]) {
      const [col, row] = key.split("-").map(Number)
      windows[key] = { col, row, start: Infinity, end: 0 }
    }
  })
  const armies = {}
  // A standoff is drawn with the board's armies at the round's end: a camp that stood there before
  // the round is in no step of the log. The stacks standing there are its armies already.
  const settled = new Set()
  Object.entries(windows).forEach(([key, window]) => {
    const fought = t >= window.start && t < window.end
    const standing = standoffFrom[key] != null && t >= standoffFrom[key]
    if (fought || standing) {
      armies[key] = fought ? {} : { ...standoffs[key] }
      clashedSites.add(key)
      if (!fought) {
        settled.add(key)
        garrisonCells.add(key)
      }
    }
  })
  const enlist = (key, stack) => {
    if (settled.has(key) || !stack.team || !stack.units) return
    const side = armies[key]
    side[stack.team] = side[stack.team] ? mergeLogUnits(side[stack.team], stack.units) : { ...stack.units }
  }

  Object.values(timeline.stacks).forEach((stack) => {
    if (t >= stack.release) return
    if (stack.garrison) {
      if (!stack.start) return
      const site = `${wrapColumn(stack.start[0], timeline.columns)}-${stack.start[1]}`
      if (armies[site]) {
        if (!stack.owned) garrisonCells.add(site)
        enlist(site, stack)
        return
      }
      // A city's icon already stands for its garrison; a camp's sprite is the timeline's to draw,
      // and the live board's own sprite on that cell waits until the clash has been shown.
      if (stack.owned) return
      garrisonCells.add(site)
    } else {
      movementIds.add(stack.id)
    }
    const position = positionAt(stack, t)
    if (!position) return
    if (!position.walking) {
      const site = `${wrapColumn(Math.round(position.col), timeline.columns)}-${Math.round(position.row)}`
      if (armies[site]) {
        enlist(site, stack)
        return
      }
    }
    sprites.push({
      id: stack.id,
      col: position.col,
      row: position.row,
      angle: position.angle,
      walking: position.walking,
      alpha: position.alpha,
      // The cells this round still has it walk (unwrapped, in order), and the cells it has walked:
      // the canvas draws its line from where it stands along `ahead`, and the stack eats it
      // (owner, 2026-10-26). `along` keeps the dashes still on the map while the line shrinks.
      ahead: position.ahead,
      along: position.along,
      endKnown: stack.endKnown || stack.relation !== "enemy",
      units: stack.units,
      team: stack.team,
      relation: stack.relation,
    })
  })

  timeline.cues.forEach((cue) => {
    const key = `${cue.col}-${cue.row}`
    if (cue.kind === "land" && t < cue.at) {
      landedCells.add(key)
      const stack = timeline.stacks[cue.id]
      if (stack?.userId != null) {
        const byUser = (landingUnits[key] = landingUnits[key] || {})
        const units = (byUser[String(stack.userId)] = byUser[String(stack.userId)] || {})
        Object.entries(stack.units || {}).forEach(([unit, n]) => {
          units[unit] = (units[unit] || 0) + (Number(n?.number ?? n) || 0)
        })
      }
    }
    if (cue.kind === "lost" && t < cue.at && cue.fromOwner != null) lostCells[key] = cue.fromOwner
    if (cue.kind === "clash" && cue.newThisRound && t < cue.at + cue.duration) clashedSites.add(key)
    if (["move", "contact", "taken", "land", "lost"].includes(cue.kind)) return
    if (t < cue.at || t >= cue.at + cue.duration) return
    effects.push({
      kind: cue.kind,
      col: cue.col,
      row: cue.row,
      progress: cue.duration > 0 ? (t - cue.at) / cue.duration : 1,
      direction: cue.direction ?? null,
    })
  })

  Object.entries(armies).forEach(([key, sides]) => {
    const { col, row } = windows[key]
    // A battle is two sides; one this side cannot read is drawn as its plain figures.
    fights.push({ col, row, armies: { human: sides.human || {}, zombie: sides.zombie || {} }, relations: timeline.relations || {} })
  })

  return { sprites, effects, fights, hiddenLive: { movementIds, landedCells, landingUnits, clashedSites, garrisonCells, lostCells }, done: t >= timeline.total }
}

/* Two `{unit: n}` dicts of one side as one; a headcount (`{count: n}`) beside anything is a headcount. */
const mergeLogUnits = (a, b) => {
  const entries = [...Object.entries(a || {}), ...Object.entries(b || {})]
  if (entries.some(([unit]) => unit === "count")) return { count: entries.reduce((sum, [, n]) => sum + (Number(n) || 0), 0) }
  const merged = {}
  entries.forEach(([unit, n]) => {
    merged[unit] = (merged[unit] || 0) + (Number(n) || 0)
  })
  return merged
}

/* The moments `hiddenLive` changes — a landing, a battle shown, a stack given back, the end — so
   the board can be rebuilt then and not every frame. Sorted, unique, the end last. */
export const boundariesOf = (timeline) => {
  if (!timeline) return []
  const times = new Set([timeline.total])
  Object.values(timeline.stacks).forEach((stack) => times.add(stack.release))
  timeline.cues.forEach((cue) => {
    if (cue.kind === "land" || cue.kind === "lost") times.add(cue.at)
    if (cue.kind === "clash") times.add(cue.at + cue.duration)
  })
  return [...times].filter((time) => Number.isFinite(time)).sort((a, b) => a - b)
}

/* Whether a round's log has a fight in it — what moves the `battle` sound off the round-turn
   moment and onto the clash (M7). */
export const motionHasClash = (motion) =>
  Array.isArray(motion?.steps) && motion.steps.some((step) => step?.kind === "battle" || step?.kind === "contact")

/* The sounds the show makes, and when (M7): `battle` on the first clash of a battle the viewer's
   side is a party to, once per site; `blast` for a strike the viewer fired or one landing on their
   side; `tunnel` on the viewer's own tunnel cues. Nothing for a stranger's fight, and nothing at
   all for a viewer with no side. `partiesAt(key)` is the board's answer to who holds or garrisons a
   cell, since the log only names the stacks. */
export const soundCues = (timeline, { viewerId, teamOf = () => null, partiesAt = () => [] } = {}) => {
  if (!timeline || viewerId == null) return []
  const myTeam = teamOf(viewerId)
  if (myTeam == null) return []
  const onMySide = (userId) =>
    userId != null && (String(userId) === String(viewerId) || teamOf(userId) === myTeam)
  const stacksAt = (key, at) =>
    Object.values(timeline.stacks)
      .filter((stack) => {
        const position = positionAt(stack, at)
        return position && `${wrapColumn(Math.round(position.col), timeline.columns)}-${Math.round(position.row)}` === key
      })
      .map((stack) => stack.userId)
  const partiesOf = (cue, key) => [
    ...(cue.ids || []).map((id) => timeline.stacks[id]?.userId),
    ...stacksAt(key, cue.at),
    ...(partiesAt(key) || []),
  ]
  const sounds = []
  const sitesHeard = new Set()
  timeline.cues.forEach((cue) => {
    const key = `${cue.col}-${cue.row}`
    if (cue.kind === "clash") {
      if (sitesHeard.has(key)) return
      sitesHeard.add(key)
      // The server's own list of the sides on the site first; the stacks and the board when it
      // sent none (an older server).
      if (cue.teams) {
        if (cue.teams.includes(myTeam)) sounds.push({ at: cue.at, sound: "battle" })
        return
      }
      const contact = timeline.cues.find((other) => other.kind === "contact" && `${other.col}-${other.row}` === key)
      const parties = [...partiesOf(cue, key), ...(contact?.ids || []).map((id) => timeline.stacks[id]?.userId)]
      if (parties.some(onMySide)) sounds.push({ at: cue.at, sound: "battle" })
    } else if (cue.kind === "blast") {
      if (onMySide(cue.by) || partiesOf(cue, key).some(onMySide)) sounds.push({ at: cue.at, sound: "blast" })
    } else if (cue.kind === "tunnel") {
      if (cue.by != null && String(cue.by) === String(viewerId)) sounds.push({ at: cue.at, sound: "tunnel" })
    }
  })
  return sounds.sort((a, b) => a.at - b.at)
}
